import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Badge,
  Button,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { ChevronRightIcon, TimeIcon, DeleteIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import { AdvisorHistory } from '../types';
import { CandleChart } from '../components/CandleChart';
import ReactMarkdown from 'react-markdown';

const API_BASE_URL = 'https://finomo-middleware.ml-meastro.workers.dev';

export const AdvisorHistoryPage: React.FC = () => {
  const [histories, setHistories] = useState<AdvisorHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedHistory, setSelectedHistory] = useState<AdvisorHistory | null>(null);
  const [viewLoading, setViewLoading] = useState<boolean>(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchHistories();
  }, []);

  const fetchHistories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advisor/history`);
      if (!response.ok) throw new Error('Failed to fetch history');
      const data = await response.json();
      // Ensure we're getting an array of histories
      const histories = Array.isArray(data) ? data : data.history || [];
      console.log('Fetched histories:', histories); // Add this for debugging
      setHistories(histories);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (historyId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/advisor/history/${historyId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete history');
      setHistories(histories.filter(h => h.historyId !== historyId));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete history');
    }
  };

  const handleViewAnalysis = (history: AdvisorHistory) => {
    setViewLoading(true);
    setSelectedHistory(history);
  };

  const handleCloseModal = () => {
    setSelectedHistory(null);
    setViewLoading(false);
  };

  const HistoryCard: React.FC<{ history: AdvisorHistory; onView: (history: AdvisorHistory) => void }> = ({ history, onView }) => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    // Add data validation
    const analysisText = history?.analysis || 'No analysis available';
    const firstLine = analysisText.split('\n')[0] || 'No details available';
    const stocksList = history?.stocks?.join(', ') || 'No stocks specified';
    const timestamp = history?.timestamp ? new Date(history.timestamp).toLocaleDateString() : 'Date not available';
    const capital = history?.capital || 0;

    return (
      <Card
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
        _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
        transition="all 0.2s"
      >
        <CardHeader>
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <HStack>
                <TimeIcon />
                <Text fontSize="sm" color="gray.500">
                  {timestamp}
                </Text>
              </HStack>
              <Text fontWeight="bold" fontSize="lg">
                {stocksList}
              </Text>
            </VStack>
            <Badge colorScheme="green" fontSize="sm">
              ${capital.toLocaleString()}
            </Badge>
          </HStack>
        </CardHeader>
        <CardBody py={2}>
          <Text noOfLines={3} fontSize="sm" color="gray.500">
            {firstLine}
          </Text>
        </CardBody>
        <CardFooter pt={2}>
          <HStack justify="space-between" width="100%">
            <Button
              size="sm"
              leftIcon={<ChevronRightIcon />}
              onClick={() => onView(history)}
              variant="ghost"
            >
              View Analysis
            </Button>
            <Button
              size="sm"
              leftIcon={<DeleteIcon />}
              onClick={() => handleDelete(history.historyId)}
              colorScheme="red"
              variant="ghost"
            >
              Delete
            </Button>
          </HStack>
        </CardFooter>
      </Card>
    );
  };

  const HistoryModal: React.FC<{ history: AdvisorHistory | null }> = ({ history }) => {
    const [isLoadingContent, setIsLoadingContent] = useState(true);
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    useEffect(() => {
      if (history) {
        const timer = setTimeout(() => {
          setIsLoadingContent(false);
          setViewLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }, [history]);

    if (!history && !viewLoading) return null;

    return (
      <Modal
        isOpen={!!history || viewLoading}
        onClose={handleCloseModal}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {viewLoading && !history ? (
              <Spinner size="sm" mr={2} />
            ) : (
              <VStack align="start" spacing={1}>
                <Text>{history?.stocks.join(', ')}</Text>
                <HStack>
                  <Badge colorScheme="blue">
                    ${history?.capital.toLocaleString()}
                  </Badge>
                  <Badge colorScheme="purple">
                    {history?.duration} {history?.duration === 1 ? 'Month' : 'Months'}
                  </Badge>
                  <Text fontSize="sm" color="gray.500">
                    {history?.timestamp && new Date(history.timestamp).toLocaleDateString()}
                  </Text>
                </HStack>
              </VStack>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {(isLoadingContent || viewLoading) ? (
              <VStack spacing={8} py={8}>
                <Spinner size="xl" />
                <Text>Loading analysis...</Text>
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} w="full">
                  {history?.stocks.map((stock) => (
                    <Box
                      key={stock}
                      height="300px"
                      bg={useColorModeValue('gray.100', 'gray.700')}
                      borderRadius="lg"
                      position="relative"
                      overflow="hidden"
                    >
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        height="100%"
                        bg={useColorModeValue('gray.200', 'gray.600')}
                        animation="pulse 1.5s infinite"
                        sx={{
                          '@keyframes pulse': {
                            '0%': { opacity: 0.6 },
                            '50%': { opacity: 0.8 },
                            '100%': { opacity: 0.6 },
                          },
                        }}
                      />
                    </Box>
                  )) || []}
                </SimpleGrid>
              </VStack>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {history.stockCharts && (
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={6}>
                    {history.stockCharts.map((chart: any) => (
                      <Box key={chart.symbol}>
                        <CandleChart
                          data={chart.data}
                          isLoading={false}
                          selectedInterval="month"
                          onIntervalChange={() => {}}
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                )}
                <Box
                  p={6}
                  bg={bgColor}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <ReactMarkdown>{history.analysis}</ReactMarkdown>
                </Box>
              </motion.div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8} centerContent>
        <Spinner size="xl" />
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading 
          textAlign="center" 
          bgGradient="linear(to-r, blue.400, purple.500)" 
          bgClip="text"
          mb={8}
        >
          Analysis History
        </Heading>

        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {histories.map((history) => (
            <HistoryCard 
              key={history.historyId} 
              history={history}
              onView={handleViewAnalysis}
            />
          ))}
        </SimpleGrid>

        {histories.length === 0 && !error && (
          <Box textAlign="center" py={10}>
            <Text color="gray.500">No analysis history found.</Text>
          </Box>
        )}

        <HistoryModal history={selectedHistory} />
      </VStack>
    </Container>
  );
};
